import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminPartnersApi, type Partner } from '@/lib/api';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/components/ui/toast';
import { Plus, ExternalLink, Building2, Users, Search, X, Handshake } from 'lucide-react';

export const Route = createFileRoute('/_auth/partners')({
  component: PartnersPage,
});

export default function PartnersPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'SUSPENDED'>('ALL');
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'partners', statusFilter],
    queryFn: () => adminPartnersApi.list({
      limit: 50,
      status: statusFilter === 'ALL' ? undefined : statusFilter,
    }),
  });

  const filtered = (data?.data ?? []).filter((p) =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.slug.includes(search.toLowerCase()),
  );

  return (
    <div className="page">
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Handshake size={22} style={{ color: '#6366f1' }} /> Partner Organisations
          </h1>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
            {data?.total ?? '—'} partners · Resellers and enterprise accounts that manage their own tenants
          </p>
        </div>
        <button
          id="new-partner-btn"
          onClick={() => setShowCreate(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px',
            background: 'var(--color-primary)', color: '#fff', border: 'none',
            borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            boxShadow: '0 2px 12px var(--color-primary-glow)',
          }}
        >
          <Plus size={15} /> New partner
        </button>
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 10, marginTop: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 340 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#475569' }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or slug…"
            style={{
              width: '100%', padding: '8px 12px 8px 34px', background: '#0f1221',
              border: '1px solid #1e2130', borderRadius: 8, color: '#e2e8f0',
              fontSize: 13, outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>
        {(['ALL', 'ACTIVE', 'SUSPENDED'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            style={{
              padding: '7px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: '1px solid',
              borderColor: statusFilter === s ? 'var(--color-primary)' : '#1e2130',
              background: statusFilter === s ? 'var(--color-primary-dim)' : 'transparent',
              color: statusFilter === s ? 'var(--color-primary)' : '#64748b',
            }}
          >
            {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {/* ── Loading ── */}
      {isLoading && <div style={{ padding: '40px 0', textAlign: 'center' }}><Spinner size="md" /></div>}

      {/* ── Empty ── */}
      {!isLoading && filtered.length === 0 && (
        <div style={{
          padding: '56px 0', textAlign: 'center', border: '1px dashed #1e2130',
          borderRadius: 12, marginTop: 16, color: '#475569',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
        }}>
          <Handshake size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: 14 }}>
            {search ? `No partners match "${search}"` : 'No partner organisations yet'}
          </p>
          {!search && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: '#334155' }}>
              Create the first partner to get started.
            </p>
          )}
        </div>
      )}

      {/* ── Table ── */}
      {filtered.length > 0 && (
        <div style={{
          marginTop: 16, overflowX: 'auto',
          border: '1px solid #1a1f35', borderRadius: 12,
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1a1f35' }}>
                {['Partner', 'Status', 'Tenants', 'Users', 'Domain', 'Created', ''].map((h) => (
                  <th key={h} style={{
                    padding: '11px 16px', textAlign: 'left',
                    color: '#475569', fontWeight: 600, fontSize: 11,
                    textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => <PartnerRow key={p.id} partner={p} onMutate={() => qc.invalidateQueries({ queryKey: ['admin', 'partners'] })} />)}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create Drawer ── */}
      {showCreate && <CreatePartnerDrawer onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ['admin', 'partners'] }); }} />}
    </div>
  );
}

// ── Partner row ──────────────────────────────────────────────────────────────

function PartnerRow({ partner, onMutate }: { partner: Partner; onMutate: () => void }) {
  const isSuspended = partner.status === 'SUSPENDED';

  const { mutate: toggle, isPending } = useMutation({
    mutationFn: () => isSuspended
      ? adminPartnersApi.activate(partner.id)
      : adminPartnersApi.suspend(partner.id),
    onSuccess: () => {
      toast.success(isSuspended ? 'Partner activated' : 'Partner suspended');
      onMutate();
    },
  });

  return (
    <tr style={{ borderBottom: '1px solid #12172a' }} className="tableRow">
      <td style={{ padding: '13px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 13, color: '#fff', flexShrink: 0,
          }}>
            {partner.name[0].toUpperCase()}
          </div>
          <div>
            <p style={{ margin: 0, fontWeight: 600, color: '#e2e8f0' }}>{partner.name}</p>
            <p style={{ margin: 0, fontSize: 11, color: '#475569' }}>@{partner.slug}</p>
          </div>
        </div>
      </td>
      <td style={{ padding: '13px 16px' }}>
        <span style={{
          padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600,
          background: isSuspended ? '#ef444420' : '#10b98120',
          color: isSuspended ? '#f87171' : '#34d399',
          border: `1px solid ${isSuspended ? '#ef444440' : '#10b98140'}`,
        }}>
          {isSuspended ? 'Suspended' : 'Active'}
        </span>
      </td>
      <td style={{ padding: '13px 16px', color: '#94a3b8' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <Building2 size={13} style={{ opacity: 0.5 }} />{partner.tenantCount}
        </span>
      </td>
      <td style={{ padding: '13px 16px', color: '#94a3b8' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <Users size={13} style={{ opacity: 0.5 }} />{partner.userCount}
        </span>
      </td>
      <td style={{ padding: '13px 16px', color: '#64748b', fontSize: 12 }}>
        {partner.allowedDomain ?? <span style={{ opacity: 0.4 }}>—</span>}
      </td>
      <td style={{ padding: '13px 16px', color: '#64748b', fontSize: 12, whiteSpace: 'nowrap' }}>
        {new Date(partner.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
      </td>
      <td style={{ padding: '13px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Link
            to="/partner/$partnerId"
            params={{ partnerId: partner.id }}
            id={`partner-view-${partner.slug}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px',
              border: '1px solid #2d3148', borderRadius: 7, color: '#94a3b8',
              fontSize: 12, fontWeight: 500, textDecoration: 'none',
            }}
          >
            <ExternalLink size={12} /> View
          </Link>
          <button
            onClick={() => toggle()}
            disabled={isPending}
            style={{
              padding: '5px 11px', borderRadius: 7, fontSize: 12, fontWeight: 500,
              cursor: 'pointer', border: '1px solid',
              borderColor: isSuspended ? '#10b98140' : '#ef444440',
              background: 'transparent',
              color: isSuspended ? '#34d399' : '#f87171',
            }}
          >
            {isPending ? <Spinner size="sm" /> : isSuspended ? 'Activate' : 'Suspend'}
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Create Drawer ────────────────────────────────────────────────────────────

function CreatePartnerDrawer({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [domain, setDomain] = useState('');
  const [autoProvision, setAutoProvision] = useState(false);

  const isValid = name.trim() && firstName.trim() && lastName.trim() && contactEmail.trim().includes('@');

  const { mutate, isPending } = useMutation({
    mutationFn: () => adminPartnersApi.create({
      name: name.trim(),
      contact: {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: contactEmail.trim(),
      },
      allowedDomain: domain.trim() || undefined,
      autoProvision,
    }),
    onSuccess: (p) => {
      toast.success(`Partner "${p.name}" created`);
      onCreated();
    },
  });

  const slugPreview = name
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50);

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(4px)', zIndex: 40,
      }} />
      {/* Drawer */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 440,
        background: '#0d1022', borderLeft: '1px solid #1e2130',
        zIndex: 50, padding: '28px 24px', overflowY: 'auto',
        boxShadow: '-16px 0 60px rgba(0,0,0,0.6)',
        animation: 'drawerIn 0.22s cubic-bezier(0.34,1.2,0.64,1)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#e2e8f0' }}>
            New Partner Organisation
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        <Field label="Partner Name" required hint="Used to derive the unique slug automatically.">
          <input
            id="partner-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Corp"
            autoFocus
            style={inputStyle}
          />
          {name && (
            <p style={{ margin: '5px 0 0', fontSize: 11, color: '#475569' }}>
              Slug: <code style={{ color: '#818cf8' }}>@{slugPreview}</code>
            </p>
          )}
        </Field>

        {/* Contact divider */}
        <div style={{ margin: '2px 0 16px', paddingBottom: 12, borderBottom: '1px solid #1a1f35' }}>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Default Contact <span style={{ color: '#f87171' }}>*</span></p>
          <p style={{ margin: '3px 0 0', fontSize: 11, color: '#334155' }}>Becomes the first admin and receives platform alerts.</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
          <Field label="First Name" required>
            <input id="contact-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Alice" style={inputStyle} />
          </Field>
          <Field label="Last Name" required>
            <input id="contact-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Virtanen" style={inputStyle} />
          </Field>
        </div>

        <Field label="Work Email" required>
          <input id="contact-email" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="alice@acme.com" style={inputStyle} />
        </Field>

        <div style={{ margin: '2px 0 16px', paddingBottom: 12, borderBottom: '1px solid #1a1f35' }}>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Optional Settings</p>
        </div>

        <Field label="Allowed Email Domain" hint="Users with this domain can be auto-matched to this partner.">
          <input
            id="partner-domain"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="acme.com"
            style={inputStyle}
          />
        </Field>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginTop: 4 }}>
          <input
            id="partner-auto-provision"
            type="checkbox"
            checked={autoProvision}
            onChange={(e) => setAutoProvision(e.target.checked)}
            style={{ width: 16, height: 16, accentColor: 'var(--color-primary)', cursor: 'pointer' }}
          />
          <span style={{ fontSize: 13, color: '#94a3b8' }}>
            Auto-provision partner users on first login
          </span>
        </label>

        <div style={{ display: 'flex', gap: 10, marginTop: 28 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '10px', background: 'transparent', border: '1px solid #1e2130',
              borderRadius: 9, color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            id="create-partner-submit"
            onClick={() => mutate()}
            disabled={!isValid || isPending}
            style={{
              flex: 2, padding: '10px', background: 'var(--color-primary)', border: 'none',
              borderRadius: 9, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              opacity: !isValid ? 0.5 : 1,
            }}
          >
            {isPending ? <Spinner size="sm" /> : 'Create partner'}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes drawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .tableRow:hover { background: #0f1221; }
      `}</style>
    </>
  );
}

function Field({ label, required, hint, children }: {
  label: string; required?: boolean; hint?: string; children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 6 }}>
        {label} {required && <span style={{ color: '#f87171' }}>*</span>}
      </label>
      {children}
      {hint && <p style={{ margin: '5px 0 0', fontSize: 11, color: '#475569' }}>{hint}</p>}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', background: '#0f1221',
  border: '1px solid #1e2130', borderRadius: 8, color: '#e2e8f0',
  fontSize: 13, outline: 'none', boxSizing: 'border-box',
};
